// Intelligent Model Selector

import { type ModelMetadata, type ProviderName, getAllModels } from './model-metadata.js'

let configuredProviders: Set<ProviderName> | null = null

interface TaskCharacteristics {
  language: 'chinese' | 'english'
  isAsianLanguage: boolean
  hasChinese: boolean
  isComplex: boolean
  needsSpeed: boolean
  isCodeTask: boolean
  needsPrivacy: boolean
  isCreative: boolean
  hasLongContext: boolean
  promptLength: number
  historyLength: number
}

export interface ConversationMessage {
  role: string
  content?: string
  [k: string]: unknown
}

export interface SelectorOptions {
  conversationHistory?: ConversationMessage[]
  availableProviders?: ProviderName[]
  prioritizeCost?: boolean
  prioritizeSpeed?: boolean
  prioritizeQuality?: boolean
  requireLocal?: boolean
  maxCostPer1M?: number
}

export interface ModelSelection {
  provider: ProviderName
  model: string
  reason: string
  characteristics: TaskCharacteristics
  score: string
  alternatives: Array<{
    provider: ProviderName
    model: string
    name: string
    score: string
  }>
}

function analyzeTaskCharacteristics(
  prompt: string,
  conversationHistory: ConversationMessage[] = [],
): TaskCharacteristics {
  const promptLower = prompt.toLowerCase()
  const promptLength = prompt.length
  const historyLength = conversationHistory.length

  const hasChinese = /[\u4e00-\u9fa5]/.test(prompt)
  const hasKorean = /[\uac00-\ud7af]/.test(prompt)
  const hasJapanese = /[\u3040-\u309f\u30a0-\u30ff]/.test(prompt)
  const isAsianLanguage = hasChinese || hasKorean || hasJapanese
  const language = hasChinese ? 'chinese' : 'english'

  const complexityKeywords = [
    'analyze',
    'explain',
    'compare',
    'evaluate',
    'reasoning',
    'complex',
    'detailed',
    'comprehensive',
    '分析',
    '解释',
    '比较',
    '评估',
    '推理',
    '复杂',
    '详细',
  ]
  const hasComplexity = complexityKeywords.some(kw => promptLower.includes(kw))
  const isComplex = hasComplexity || promptLength > 500

  const speedKeywords = [
    'quick',
    'fast',
    'rapid',
    'immediate',
    'urgent',
    'asap',
    '快速',
    '立即',
    '马上',
    '紧急',
  ]
  const needsSpeed = speedKeywords.some(kw => promptLower.includes(kw))

  const codeKeywords = [
    'code',
    'program',
    'function',
    'script',
    'implement',
    'debug',
    '代码',
    '编程',
    '函数',
    '实现',
    '调试',
  ]
  const isCodeTask = codeKeywords.some(kw => promptLower.includes(kw))

  const privacyKeywords = [
    'private',
    'confidential',
    'secure',
    'offline',
    'local',
    '隐私',
    '保密',
    '离线',
    '本地',
  ]
  const needsPrivacy = privacyKeywords.some(kw => promptLower.includes(kw))

  const creativeKeywords = [
    'creative',
    'write',
    'story',
    'poem',
    'brainstorm',
    'idea',
    '创意',
    '创作',
    '写作',
    '故事',
    '灵感',
  ]
  const isCreative = creativeKeywords.some(kw => promptLower.includes(kw))

  const hasLongContext = historyLength > 10 || promptLength > 1000

  return {
    language,
    isAsianLanguage,
    hasChinese,
    isComplex,
    needsSpeed,
    isCodeTask,
    needsPrivacy,
    isCreative,
    hasLongContext,
    promptLength,
    historyLength,
  }
}

function calculateModelScore(
  model: ModelMetadata,
  characteristics: TaskCharacteristics,
  options: SelectorOptions,
): number {
  const { hasChinese, isComplex, needsSpeed, needsPrivacy, hasLongContext } = characteristics
  const { prioritizeCost, prioritizeSpeed, prioritizeQuality } = options

  let score = 50

  if (hasChinese) {
    score += model.performance.chineseScore * 3
  } else {
    score += model.performance.englishScore * 2
  }

  if (needsSpeed) {
    score += model.performance.speedScore * 4
  } else {
    score += model.performance.speedScore * 1.5
  }

  if (isComplex) {
    score += model.performance.qualityScore * 4
  } else {
    score += model.performance.qualityScore * 2
  }

  if (needsPrivacy && model.cost.local) {
    score += 20
  } else if (needsPrivacy && !model.cost.local) {
    score -= 15
  }

  if (model.cost.free) score += 5
  if (model.cost.local) score += 5

  if (prioritizeCost) {
    if (model.cost.free) {
      score += 15
    } else if (model.cost.inputPer1MTokens < 1.0) {
      score += 10
    } else {
      score -= model.cost.inputPer1MTokens * 2
    }
  }

  if (hasLongContext) {
    const contextScore = Math.min(model.limits.contextWindow / 100000, 10)
    score += contextScore * 2
  }

  if (prioritizeSpeed) {
    score += model.performance.speedScore * 3
  }
  if (prioritizeQuality) {
    score += model.performance.qualityScore * 3
  }

  if (model.capabilities.toolCalling) {
    score += 10
  }

  return Math.max(0, Math.min(100, score))
}

export function setAvailableProviders(providers: ProviderName[]): void {
  if (!Array.isArray(providers)) {
    throw new Error('providers must be an array')
  }
  configuredProviders = new Set(providers)
}

function getAvailableModels(providers?: ProviderName[]): ModelMetadata[] {
  let models = getAllModels()

  const availableSet = providers ? new Set(providers) : configuredProviders

  models = models.filter(model => {
    if (model.provider === 'ollama') return true
    if (!availableSet) return true
    return availableSet.has(model.provider)
  })

  return models
}

export function selectBestModel(prompt: string, options: SelectorOptions = {}): ModelSelection {
  const {
    conversationHistory = [],
    availableProviders,
    prioritizeSpeed = false,
    prioritizeQuality = false,
    requireLocal = false,
    maxCostPer1M = Number.POSITIVE_INFINITY,
  } = options

  const characteristics = analyzeTaskCharacteristics(prompt, conversationHistory)

  let availableModels = getAvailableModels(availableProviders)

  if (availableModels.length === 0) {
    throw new Error('No models available. Please configure API keys or install Ollama.')
  }

  if (requireLocal || characteristics.needsPrivacy) {
    availableModels = availableModels.filter(m => m.cost.local)
  }

  if (maxCostPer1M < Number.POSITIVE_INFINITY) {
    availableModels = availableModels.filter(m => m.cost.inputPer1MTokens <= maxCostPer1M)
  }

  availableModels = availableModels.filter(m => m.capabilities.toolCalling)

  if (availableModels.length === 0) {
    throw new Error('No models match the specified requirements')
  }

  const scoredModels = availableModels.map(model => ({
    model,
    score: calculateModelScore(model, characteristics, options),
  }))

  scoredModels.sort((a, b) => b.score - a.score)

  const best = scoredModels[0]
  const selectedModel = best.model

  const reasons: string[] = []

  if (characteristics.hasChinese) {
    reasons.push(`Chinese language (score: ${selectedModel.performance.chineseScore}/10)`)
  }

  if (characteristics.needsSpeed || prioritizeSpeed) {
    reasons.push(`Speed requirement (score: ${selectedModel.performance.speedScore}/10)`)
  }

  if (characteristics.isComplex || prioritizeQuality) {
    reasons.push(`Quality requirement (score: ${selectedModel.performance.qualityScore}/10)`)
  }

  if (characteristics.needsPrivacy || requireLocal) {
    reasons.push('Privacy/local requirement')
  }

  if (selectedModel.cost.free) {
    reasons.push('Cost-efficient (free)')
  }

  const reason = reasons.length > 0 ? reasons.join(', ') : 'Best overall match'

  return {
    provider: selectedModel.provider,
    model: selectedModel.modelId,
    reason,
    characteristics,
    score: best.score.toFixed(1),
    alternatives: scoredModels.slice(1, 3).map(s => ({
      provider: s.model.provider,
      model: s.model.modelId,
      name: s.model.name,
      score: s.score.toFixed(1),
    })),
  }
}

type SortFn = (a: ModelMetadata, b: ModelMetadata) => number
type FilterFn = ((m: ModelMetadata) => boolean) | null

function findBestModel(
  filterFn: FilterFn,
  sortFn: SortFn,
  availableProviders?: ProviderName[],
): { provider: ProviderName; model: string } | null {
  const models = getAvailableModels(availableProviders)
  const filtered = models.filter(m => m.capabilities.toolCalling && (!filterFn || filterFn(m)))
  if (filtered.length === 0) return null

  const best = filtered.sort(sortFn)[0]
  return { provider: best.provider, model: best.modelId }
}

export const modelPresets = {
  fast: () => findBestModel(null, (a, b) => b.performance.speedScore - a.performance.speedScore),
  quality: () =>
    findBestModel(null, (a, b) => b.performance.qualityScore - a.performance.qualityScore),
  free: () =>
    findBestModel(
      m => m.cost.free,
      (a, b) => b.performance.qualityScore - a.performance.qualityScore,
    ),
  local: () =>
    findBestModel(
      m => m.cost.local,
      (a, b) => b.performance.qualityScore - a.performance.qualityScore,
    ),
  chinese: () =>
    findBestModel(null, (a, b) => b.performance.chineseScore - a.performance.chineseScore),
}
